import React from "react";
import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { 
    padding: 10, 
    fontSize: 9,
    paddingTop: 20,
    // Ensure A4 size is properly used
    size: "A4",
    orientation: "landscape" // Changed to landscape for more horizontal space
  },
  header: { 
    textAlign: "center", 
    marginBottom: 20 
  },
  title: { 
    fontSize: 14, 
    fontWeight: "bold", 
    marginBottom: 2 
  },
  subtitle: { 
    fontSize: 11, 
    marginBottom: 10 
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableHeader: {
    backgroundColor: "#ffffff",
    flexDirection: "row",
  },
  tableRow: { 
    flexDirection: "row",
    minHeight: 20,
  },
  col: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 4,
    textAlign: "center",
    // Added to prevent text overflow
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  bold: { 
    fontWeight: "bold" 
  },
  leftAlign: {
    textAlign: "left",
    paddingLeft: 6,
  },
  // Increased column widths to prevent overlap
  colSerial: { width: "5%" },
  colName: { width: "18%" },
  colWorkPermit: { width: "12%" },
  colPersonal: { width: "12%" },
  colBankEx: { width: "10%" },
  colBankName: { width: "12%" },
  colBankDetails: { width: "18%" },
  colAbsent: { width: "8%" },
  colFixed: { width: "10%" },
  colVariable: { width: "10%" },
  colTotal: { width: "10%" },
  // Style for merged columns in footer
  mergedCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 4,
    textAlign: "center",
  },
  totalLabelCol: {
    width: "65%", // Merges columns from Name to Bank Details
    textAlign: "right",
    paddingRight: 10,
  },
  // Additional styles for better text handling
  wrapText: {
    flexWrap: 'wrap',
    wordBreak: 'break-word',
  },
  smallText: {
    fontSize: 8,
  },
});

const PayslipBatchPDF = ({ companyName, month, year, payslips }) => {
  // Calculate totals
  const totals = payslips.reduce(
    (acc, p) => {
      const fixedPortion = parseFloat(p.basic || 0);
      const variablePortion =
        parseFloat(p.overtime_pay || 0) +
        parseFloat(p.bonus || 0) +
        parseFloat(p.total_allowances || 0);
      const totalPayment =
        fixedPortion +
        variablePortion -
        (parseFloat(p.total_deductions || 0) +
          parseFloat(p.loan_deduction || 0));

      acc.fixed += fixedPortion;
      acc.variable += variablePortion;
      acc.total += totalPayment;
      return acc;
    },
    { fixed: 0, variable: 0, total: 0 }
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{companyName}</Text>
          <Text style={styles.subtitle}>
            PAYROLL FOR THE MONTH - {month} {year}
          </Text>
        </View>

        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.col, styles.colSerial, styles.bold]}>Sl. No</Text>
            <Text style={[styles.col, styles.colName, styles.bold, styles.leftAlign]}>Name of the Employee</Text>
            <Text style={[styles.col, styles.colWorkPermit, styles.bold]}>Work Permit No</Text>
            <Text style={[styles.col, styles.colPersonal, styles.bold]}>Personal No</Text>
            <Text style={[styles.col, styles.colBankEx, styles.bold]}>Bank/EX</Text>
            <Text style={[styles.col, styles.colBankName, styles.bold]}>Bank Name</Text>
            <Text style={[styles.col, styles.colBankDetails, styles.bold, styles.leftAlign]}>
              FAB/IBAN
            </Text>
            <Text style={[styles.col, styles.colAbsent, styles.bold]}>Absent Days</Text>
            <Text style={[styles.col, styles.colFixed, styles.bold]}>Fixed</Text>
            <Text style={[styles.col, styles.colVariable, styles.bold]}>Variable</Text>
            <Text style={[styles.col, styles.colTotal, styles.bold]}>Total</Text>
          </View>

          {/* Table Rows */}
          {payslips.map((p, index) => {
            const fixedPortion = parseFloat(p.basic || 0);
            const variablePortion =
              parseFloat(p.overtime_pay || 0) +
              parseFloat(p.bonus || 0) +
              parseFloat(p.total_allowances || 0);
            const totalPayment =
              fixedPortion +
              variablePortion -
              (parseFloat(p.total_deductions || 0) +
                parseFloat(p.loan_deduction || 0));

            return (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.col, styles.colSerial]}>{index + 1}</Text>
                <Text style={[styles.col, styles.colName, styles.leftAlign, styles.wrapText]}>
                  {p.employee?.first_name} {p.employee?.last_name}
                </Text>
                <Text style={[styles.col, styles.colWorkPermit, styles.smallText]}>
                  {p.employee?.work_permit_no || "-"}
                </Text>
                <Text style={[styles.col, styles.colPersonal, styles.smallText]}>
                  {p.employee?.personal_no || "-"}
                </Text>
                <Text style={[styles.col, styles.colBankEx]}>
                  {p.employee?.payment_method || "Bank"}
                </Text>
                <Text style={[styles.col, styles.colBankName, styles.smallText]}>
                  {p.employee?.bank_name || p.employee?.exchange_name || "-"}
                </Text>
                <Text style={[styles.col, styles.colBankDetails, styles.leftAlign, styles.smallText]}>
                  {p.employee?.bank_account_no || p.employee?.reference_no || "-"}
                </Text>
                <Text style={[styles.col, styles.colAbsent]}>
                  {p.absent_days || "0"}
                </Text>
                <Text style={[styles.col, styles.colFixed]}>
                  {fixedPortion.toFixed(2)}
                </Text>
                <Text style={[styles.col, styles.colVariable]}>
                  {variablePortion.toFixed(2)}
                </Text>
                <Text style={[styles.col, styles.colTotal]}>
                  {totalPayment.toFixed(2)}
                </Text>
              </View>
            );
          })}

          {/* Footer Row with merged columns */}
          <View style={[styles.tableRow, styles.bold]}>
            {/* <Text style={[styles.col, styles.colSerial]}></Text> */}
            <Text style={[styles.mergedCol, styles.totalLabelCol]}>
              Grand Total
            </Text>
            <Text style={[styles.col, styles.colAbsent]}></Text>
            <Text style={[styles.col, styles.colFixed]}>
              {totals.fixed.toFixed(2)}
            </Text>
            <Text style={[styles.col, styles.colVariable]}>
              {totals.variable.toFixed(2)}
            </Text>
            <Text style={[styles.col, styles.colTotal]}>
              {totals.total.toFixed(2)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PayslipBatchPDF;